import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import { FormModal, Field, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import { formatDateTime } from '../utils/dateFormat';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import QcReviewerPicks, {
  planFor, reviewerPicksOf, reviewerPickGaps, appendReviewerPicks,
} from '../components/workflow/QcReviewerPicks';
import { AMENDMENT_FIELD_LABELS } from '../components/workflow/constants';
import { EngagementNameList, CoverageDiffValue, fmtAmendValueRich } from '../components/workflow/CoverageDisplay';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { answerableQuestions, expandAnswerChanges, fmtQcAnswerValue, qcAnswerLabels, qcAnswerGaps, qcSplitGaps, engagementNameMap, feeAnswersChanged } from './qcQuestions';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { QcFormView } from '../components/workflow/QcForm';
import { QcaChangeForm } from './WorkflowQcaNew';

const fmtQcAnswer = fmtQcAnswerValue;

// Before/after questionnaire comparison for an answers change. Only questions
// that have a value on either side (or that differ) are shown.
const AnswersCompare = ({ change, qcType, engagementNames = {} }) => {
  const [open, setOpen] = useState(false);
  const blank = (v) => v === undefined || v === null || String(v).trim() === '';
  // The per-engagement Estimated fees expand into one row per engagement.
  const rows = answerableQuestions(qcType).flatMap((q) => {
    const bv = (change.before || {})[q.id];
    const av = (change.after || {})[q.id];
    if (q.type === 'split') {
      const sb = bv && typeof bv === 'object' ? bv : {};
      const sa = av && typeof av === 'object' ? av : {};
      return [...new Set([...Object.keys(sb), ...Object.keys(sa)])].map((eid) => ({
        id: `${q.id}.${eid}`,
        label: `${q.item.label} — ${engagementNames[eid] || `Engagement #${eid}`}`,
        type: 'number', before: sb[eid], after: sa[eid],
      }));
    }
    return blank(bv) && blank(av) ? [] : [{ ...q, before: bv, after: av }];
  });
  return (
    <div className="border border-slate-200 rounded-lg">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-1.5 px-4 py-2.5 bg-slate-50 rounded-lg text-left text-sm font-medium text-slate-700">
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
        QC6 / QC7 Form — Current vs Requested
      </button>
      {open && (
        <table className="w-full table-fixed text-sm border-t border-slate-200">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="px-4 py-1.5 font-medium w-1/2">Question</th>
              <th className="px-4 py-1.5 font-medium w-1/4">Current</th>
              <th className="px-4 py-1.5 font-medium w-1/4">Requested</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((q) => {
              const b = fmtQcAnswer(q, q.before);
              const a = fmtQcAnswer(q, q.after);
              const diff = b !== a;
              return (
                <tr key={q.id} className={diff ? 'bg-amber-50/40' : ''}>
                  <td className="px-4 py-1.5 text-slate-700 align-top break-words">{q.label}</td>
                  <td className="px-4 py-1.5 text-slate-500 align-top break-words">{b}</td>
                  <td className={`px-4 py-1.5 align-top break-words ${diff ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>{a}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const WorkflowQcaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/qca/${id}`),
        fetchJSON(`/api/workflow/qca/${id}/events`),
      ]);
      setData(detail);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/qca/${id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const request = data?.request;
  const { currentRecord, review, canReview, canModify, canDelete, engagementLock } = data || {};
  const payloadEntries = request ? Object.entries(request.payload || {}).filter(([f]) => f !== 'answers') : [];
  const answersChange = request ? (request.payload || {}).answers || null : null;
  // Names of every engagement the record covers or the amendment adds (History labels).
  const engagementNames = engagementNameMap(currentRecord?.engagements, request?.payload?.engagements?.addedEngagements);

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  const qcaLabels = useMemo(
    () => ({ ...AMENDMENT_FIELD_LABELS, ...qcAnswerLabels(currentRecord?.qcType) }),
    [currentRecord?.qcType]
  );
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    // 母單的驗收表題目也要叫得出名字（同 QC 詳情頁）。
    labels: qcaLabels,
    amendment: true,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/qca"
      backLabel="QC Amendment"
      loading={!data}
      error={error}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="QC Amendment"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            lock={engagementLock}
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={busy}
            events={events}
            onApprove={canReview && request.status === 'requested' ? () => act('approve') : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && request.status === 'awaiting_info' ? () => act('resubmit') : null}
            onDelete={canDelete ? () => setDeleting(true) : null}
          />

          <ChangesSinceReturnCard
            events={events}
            amendment
            changeLabels={AMENDMENT_FIELD_LABELS}
            fmtChangeValue={fmtAmendValueRich}
            expandChanges={(c) => expandAnswerChanges(c, currentRecord?.qcType, { engagementNames })}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title="QC Record">
            <p className="text-sm text-slate-800 break-words">{request.recordLabel}</p>
            {!currentRecord && <p className="text-xs text-amber-600 mt-1">The QC record no longer exists.</p>}
            {(currentRecord?.engagements || []).length > 0 && (
              <div className="text-sm text-slate-700 mt-2">
                <p className="text-xs text-slate-500 mb-0.5">
                  Currently Covers ({currentRecord.engagements.length})
                </p>
                <EngagementNameList
                  items={currentRecord.engagements}
                  keyOf={(e, i) => e.engagementId || i}
                />
              </div>
            )}
            <p className="text-xs text-slate-500 mt-2 break-words"><span className="font-medium text-slate-600">Reason:</span> {request.reason}</p>
          </SectionCard>

          {/* 被拉進來簽核的合夥人簽的是「我同意
              我的案子用這份 acceptance」——那份問卷當初怎麼答的必須攤在他眼前，
              否則這一關等於簽了一份沒看過的東西。 */}
          {currentRecord?.answers && Object.keys(currentRecord.answers).length > 0 && (
            <SectionCard title={`Current Form ${currentRecord.qcType}`} collapsible defaultOpen={false}>
              <QcFormView qcType={currentRecord.qcType} answers={currentRecord.answers} engagements={currentRecord.engagements || []} />
            </SectionCard>
          )}

          <SectionCard title="Requested Changes">
            {payloadEntries.length === 0 && !answersChange ? (
              <p className="text-sm text-slate-500">No field changes recorded.</p>
            ) : (
              <div className="space-y-3">
                {payloadEntries.length > 0 && (
                  <table className="w-full table-fixed text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500">
                        <th className="px-3 py-1.5 font-medium w-1/3">Field</th>
                        <th className="px-3 py-1.5 font-medium w-1/3">Current</th>
                        <th className="px-3 py-1.5 font-medium w-1/3">Requested</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {payloadEntries.map(([field, diff]) => (
                        // group：逐列的評論入口平時藏著，跟 QC 驗收表同一條規則。
                        <tr key={field} className="group">
                          <td className="px-3 py-1.5 text-slate-700 align-top break-words">
                            {AMENDMENT_FIELD_LABELS[field] || field}
                            <FieldCommentAnchor
                              field={field}
                              label={AMENDMENT_FIELD_LABELS[field] || field}
                              className="mt-0.5"
                              compact
                              hoverOnly
                            />
                          </td>
                          <td className="px-3 py-1.5 text-slate-500 align-top break-words">
                            {field === 'engagements'
                              ? <CoverageDiffValue before={diff.before} after={diff.after} side="before" />
                              : fmtAmendValueRich(field, diff.before)}
                          </td>
                          <td className="px-3 py-1.5 text-slate-900 font-medium align-top break-words">
                            {field === 'engagements'
                              ? <CoverageDiffValue before={diff.before} after={diff.after} side="after" />
                              : fmtAmendValueRich(field, diff.after)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {answersChange && <AnswersCompare change={answersChange} qcType={currentRecord?.qcType} engagementNames={engagementNames} />}
              </div>
            )}
          </SectionCard>

          {(request.files || []).length > 0 && (
            <SectionCard title="Attachments">
              <AttachmentsList
                files={request.files}
                downloadHref={(f) => `${API_BASE_URL}/api/workflow/qca/${id}/files/${f.id}`}
                badgeFor={(f) => (f.docType === 'QC_FORM'
                  ? ['Replacement QC document', 'bg-blue-50 text-blue-700']
                  : ['Supporting', 'bg-slate-100 text-slate-600'])}
              />
              <FieldCommentAnchor field="files" label="Attachments" />
            </SectionCard>
          )}

          <HistoryCard
            events={events}
            actionLabels={{ applied: ['Applied to QC record', 'bg-green-50 text-green-700'] }}
            changeLabels={AMENDMENT_FIELD_LABELS}
            fmtChangeValue={fmtAmendValueRich}
            expandChanges={(c) => expandAnswerChanges(c, currentRecord?.qcType, { engagementNames })}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => { await act('reject', { reason, fieldComments }); setRejecting(false); fc.clear(); }}
            />
          )}

          {modifying && currentRecord && (
            <ModifyModal
              request={request}
              events={events}
              record={currentRecord}
              onClose={() => setModifying(false)}
              onDone={() => { setModifying(false); load(); }}
            />
          )}

          {deleting && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="Withdraw and delete this amendment request? Its uploads are removed too."
              onClose={() => setDeleting(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/qca/${id}`, { method: 'DELETE' });
                navigate('/workflow/qca');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify = the same change form, prefilled from the requested payload (falling
// back to the record's live values). Save only — re-submit is a separate step.
const ModifyModal = ({ request, events = [], record, onClose, onDone }) => {
  const p = request.payload || {};
  const [picks, setPicks] = useState(() => reviewerPicksOf(request));
  const [form, setForm] = useState({
    // Coverage lives on engagements. The draft's own additions come
    // back from the payload; the record's existing coverage is filled in by the
    // fetch below (it is the picker's locked floor).
    engagementIds: [
      ...(record.engagements || []).map((e) => e.engagementId).filter(Boolean),
      ...(p.engagements?.addedEngagementIds || []),
    ],
    answers: p.answers?.after || record.answers || {},
    remarks: p.remarks !== undefined ? (p.remarks.after || '') : (record.remarks || ''),
  });
  const [engagements, setEngagements] = useState([]);
  const [coveredEngagementIds, setCoveredEngagementIds] = useState([]);
  const [current, setCurrent] = useState(null); // the record's current rating / EQR (addenda included)
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [reason, setReason] = useState(request.reason || '');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // 候選公司跟著記錄走——不打 /api/workflow/qc/options 取
        // entities（那支不回這個欄位）。
        const rec = await fetchJSON(`/api/workflow/qca/record/${record.id}`);
        setEngagements(rec.engagements || []);
        setCoveredEngagementIds(rec.coveredEngagementIds || []);
        setCurrent({ risk: rec.risk || null, eqr: rec.eqr || null });
      } catch { /* picker degrades to snapshot display */ }
    })();
  }, [record.id]);

  // Who reviews follows the form as edited here (lenient for a record that
  // never answered the routing questions).
  const plan = useMemo(() => planFor(form.answers, record.qcType, { lenient: true }), [form.answers, record.qcType]);
  // 匯入記錄要整張表——缺題就地標紅（同 New 那一面），不等後端 400。
  const gaps = useMemo(
    () => [
      ...(record.legacyImport ? qcAnswerGaps(form.answers, record.qcType) : []),
      ...qcSplitGaps(form.answers, record.qcType, form.engagementIds, { requireFee: feeAnswersChanged(record.answers, form.answers, record.qcType) }),
      ...reviewerPickGaps(plan, picks),
    ],
    [record, form.answers, form.engagementIds, plan, picks]
  );
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    setSaving(true);
    try {
      const fd = new FormData();
      if ((form.engagementIds || []).length > 0) fd.set('engagementIds', JSON.stringify(form.engagementIds));
      if (record.submissionMethod === 'form') fd.set('answers', JSON.stringify(form.answers || {}));
      fd.set('remarks', form.remarks || '');
      if (reason.trim()) fd.set('reason', reason.trim());
      appendReviewerPicks(fd, plan, picks);
      appendTo(fd);
      await fetchJSON(`/api/workflow/qca/${request.id}`, { method: 'PATCH', body: fd });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
      <ReturnCommentsBanner events={events} />
      {(error || validation.error) && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
          <button type="button" onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <p className="text-sm text-slate-600 break-words">{request.recordLabel}</p>
      <QcaChangeForm
        record={record} form={form} setForm={setForm}
        engagements={engagements} coveredEngagementIds={coveredEngagementIds}
        current={current}
      />
      <EditableAttachments
        files={request.files || []}
        removeIds={removeIds}
        onToggle={toggle}
        downloadHref={(f) => `${API_BASE_URL}/api/workflow/qca/${request.id}/files/${f.id}`}
        badgeFor={(f) => (f.docType === 'QC_FORM'
          ? ['Replacement QC document', 'bg-blue-50 text-blue-700']
          : ['Supporting', 'bg-slate-100 text-slate-600'])}
        label="Files on This Amendment"
      />
      <FieldCommentInline field="files" />
      <Field label="Reason" field="reason" required>
        <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
      </Field>
      <QcReviewerPicks
        plan={plan}
        picks={picks}
        onChange={setPicks}
        qcType={record.qcType}
        hint="You pick who reviews this amendment. Changing it voids recorded approvals."
      />
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowQcaDetail;
